/**
 * Utility class for PKCE (Proof Key for Code Exchange) operations.
 * Following RFC 7636 standards.
 */
export class PkceUtils {
  /**
   * The RFC 7636 allowed characters for the code_verifier.
   */
  private static readonly CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

  /**
   * Generates a high-entropy cryptographic random string.
   */
  static generateCodeVerifier(length: number = 64): string {
    const verifierLength = Math.max(43, Math.min(length, 128));
    const values = new Uint32Array(verifierLength);
    window.crypto.getRandomValues(values);

    return Array.from(values)
      .map((x) => this.CHARSET[x % this.CHARSET.length])
      .join('');
  }

  /**
   * Creates a SHA-256 hash of the verifier and encodes it to Base64Url.
   */
  static async deriveCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await window.crypto.subtle.digest('SHA-256', data);

    return this.base64UrlEncode(hash);
  }

  /**
   * Generates a random state to prevent CSRF attacks.
   */
  static generateState(): string {
    return this.generateCodeVerifier(32);
  }

  /**
   * Helper: Encodes an ArrayBuffer to a Base64Url string (removes +, /, and =).
   */
  private static base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
